import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import SpinnerButton from '@/components/common/SpinnerButton';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
} from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import HorizontalLogo from '@/components/common/HorizontalLogo';
import Page from '@/components/layout/Page';
import Input from '@/components/form/Input';
import { usePostResetPassword } from '@/data/user';

const formSchema = z
  .object({
    password: z
      .string()
      .min(6, { message: 'Password must be at least 6 characters' }),
    confirmPassword: z.string()
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword']
  });

const ResetPasswordPage = () => {
  const { resetPasswordToken } = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const postResetPassword = usePostResetPassword();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  });

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setError('');
      await postResetPassword(resetPasswordToken, {
        password: values.password
      });
      setSuccess(true);
      setSubmitting(false);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <div className="p-8">
        <HorizontalLogo />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Reset Password</CardTitle>
          <CardDescription>Enter your new password</CardDescription>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent>
              {!success && (
                <>
                  <Input
                    name="password"
                    label="New Password"
                    type="password"
                    placeholder="password"
                  />
                  <Input
                    name="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    placeholder="confirm password"
                  />
                </>
              )}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle size={18} />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {success && (
                <Alert>
                  <AlertTitle>Success</AlertTitle>
                  <AlertDescription>
                    Your password has been reset. You can now login with your
                    new password.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
            <CardFooter className="flex justify-end">
              {!success && (
                <SpinnerButton type="submit" loading={submitting}>
                  Reset Password
                </SpinnerButton>
              )}
            </CardFooter>
          </form>
        </Form>
      </Card>
      <div className="flex justify-center py-8"></div>
    </Page>
  );
};

export default ResetPasswordPage;
